'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { useSocket } from '@/providers/SocketContext'
import { useNotificationPrefs } from '@/hooks/notifications/useNotificationPrefs'
import { channelIdFromPathname } from '@/lib/chatbotRoute'
import {
  channelActivityPrefKey,
  channelActivityTarget,
  isKnownChannelActivity,
  shouldNotifySystemForChannelActivity,
  shouldToastChannelActivity,
  summarizeChannelActivity,
  summarizeChannelActivityForSystem,
} from '@/lib/notifications/channelActivity'
import { notifySystem, playNotificationSound } from '@/lib/notifications/notify'
import type { ChannelActivityPref } from '@/lib/notifications/prefs'
import type { ChannelActivityEvent } from '@/types/SocketEvents'

/** Janela de silêncio: o back NÃO coalesce `channel_activity` (cada mensagem é fato
 *  distinto) e o agrupamento é decisão do front. Sem ela, rajada = N toasts.
 *  Mesmo valor do `useBulkSendResultToast`. */
const FLUSH_DELAY_MS = 2000
/** Mesmo prazo do `useChatMoved`: transferência exige leitura, mensagem não. */
const MOVED_TOAST_DURATION_MS = 8000

/**
 * A bandeja recebe TÍTULO, não corpo: `channel_activity` não carrega o texto da
 * mensagem (invariante de [[../lib/notifications/channelActivity]]) e este módulo
 * não o inventa. Corpo de enfeite custaria a segunda linha da notificação
 * recolhida sem dizer nada que o título já não diga.
 */
const SEM_CORPO = ''

/** Um lote da janela de silêncio. `prefKey` é capturada na CHEGADA do primeiro
 *  evento: rota e visibilidade podem mudar dentro dos 2s, e quem decide sobre o
 *  aviso é o estado de quando o fato aconteceu, não o de quando o timer venceu. */
interface Lote {
  eventos: ChannelActivityEvent[]
  prefKey: ChannelActivityPref
}

/**
 * Transforma `channel_activity` em aviso, app-wide: notificação do SO e, quando
 * ela não sai, toast. Montado UMA vez em `PrivateProviders` — o atendente pode
 * estar em qualquer rota quando o evento chega.
 *
 * **Desde 2026-09-04 este hook é o produtor de notificação do SO fora da tela do
 * canal.** O `ChannelProvider` — o outro produtor — só existe em
 * `/chatbot/[channelId]`, então em `/conta`, `/admin`, `/notifications` e na home
 * de canais mensagem nova não gerava notificação nenhuma, em plataforma alguma.
 * Quem manda quando os dois veem o mesmo fato está em
 * `shouldNotifySystemForChannelActivity`.
 *
 * **Toast é fallback, não adição** (D-2, mesma regra do `useNotification`): sai só
 * quando a notificação do SO NÃO saiu, decidido pelo `outcome` tipado.
 *
 * O **badge** de não lidas não passa por aqui e nunca é opcional: badge é estado, a
 * preferência governa só o aviso.
 */
export function useChannelActivityToast(): void {
  const { socket } = useSocket()
  const router = useRouter()
  const pathname = usePathname()
  const { prefs } = useNotificationPrefs()

  // Refs: o listener vive num effect de deps enxutas (precisa sobreviver à
  // reconexão) e não pode capturar rota nem preferência stale. Molde do `useChatMoved`.
  // `router` também vai por ref: mantê-lo fora das deps do effect principal evita
  // reassinar o listener a cada render em que a instância do router mudar (o
  // `next/navigation` real é estável, mas nada nesta função depende disso).
  const pathnameRef = useRef(pathname)
  const prefsRef = useRef(prefs.channelActivity)
  const routerRef = useRef(router)
  useEffect(() => { pathnameRef.current = pathname }, [pathname])
  useEffect(() => { prefsRef.current = prefs.channelActivity }, [prefs.channelActivity])
  useEffect(() => { routerRef.current = router }, [router])

  const bufferRef = useRef<Map<string, Lote>>(new Map())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Guarda SÓ em `socket`, nunca em `socketReady` — mesma razão dos outros listeners
  // de socket do repo.
  useEffect(() => {
    if (!socket) return

    const toastar = (lote: Lote, alvo: { url: string }) => {
      const [primeiro] = lote.eventos
      toast.info(summarizeChannelActivity(lote.eventos), {
        ...(primeiro.kind !== 'new_message' && { duration: MOVED_TOAST_DURATION_MS }),
        // `sonner@2.0.7` NÃO tem `onClick` no toast (`ToastT` só declara
        // `action: Action | ReactNode`), então "clicar pra abrir" é um botão de
        // ação. `toast.custom` daria a área inteira, mas custaria um componente novo.
        // O destino é o MESMO da notificação: as duas superfícies anunciam o mesmo
        // fato e discordar sobre onde o aviso leva é bug de superfície.
        action: {
          label: 'Abrir',
          onClick: () => routerRef.current.push(alvo.url),
        },
      })
    }

    /** Um lote vira UM aviso. Async porque o toast só pode ser decidido depois de
     *  saber se a notificação do SO saiu (D-2). */
    const anunciar = async (lote: Lote) => {
      const alvo = channelActivityTarget(lote.eventos)

      // Fato que pertence ao `ChannelProvider`: este hook se abstém INTEIRO —
      // nem notificação, nem som, nem toast.
      //
      // O toast também sai daqui desde 2026-09-04. Ele saía incondicionalmente,
      // sem consultar o `outcome` do outro produtor, e produzia o duplo que a
      // Task 11 e o D-2 proíbem: janela minimizada, o provider manda a
      // notificação do SO, este hook toasta na janela escondida, o operador
      // clica na notificação e encontra o toast do mesmo fato ao restaurar.
      // O provider cobre as duas superfícies — `useNotification` já faz o toast
      // de fallback pelo `outcome` — e passou a avisar também com a aba
      // escondida, inclusive com a conversa aberta na rota.
      if (!shouldNotifySystemForChannelActivity(lote.prefKey)) return

      playNotificationSound()
      const resultado = await notifySystem(
        {
          title: summarizeChannelActivityForSystem(lote.eventos),
          body: SEM_CORPO,
          tag: alvo.tag,
          url: alvo.url,
        },
        'canal',
      )

      if (resultado.outcome !== 'sent') toastar(lote, alvo)
    }

    const flush = () => {
      timerRef.current = null
      const lotes = [...bufferRef.current.values()]
      bufferRef.current = new Map()

      for (const lote of lotes) {
        if (lote.eventos.length === 0) continue
        // `void` com `catch`: um lote que falhe não pode abortar o loop e fazer os
        // demais grupos do MESMO flush perderem o aviso — o buffer já foi zerado e
        // não há segunda chance.
        void anunciar(lote).catch(() => undefined)
      }
    }

    const onActivity = (evt: ChannelActivityEvent) => {
      // Kind fora do contrato morre aqui, antes do buffer — ver `isKnownChannelActivity`.
      if (!isKnownChannelActivity(evt)) return

      const contexto = {
        currentChannelId: channelIdFromPathname(pathnameRef.current),
        tabVisible: document.visibilityState === 'visible',
      }
      const podeAvisar = shouldToastChannelActivity(evt, {
        ...contexto,
        prefs: prefsRef.current,
      })
      if (!podeAvisar) return

      // A preferência governa os DOIS canais de aviso: quem desligou "mensagem nova
      // em outro canal" não passa a receber notificação do SO por causa disto.
      const prefKey = channelActivityPrefKey(evt, contexto)
      if (!prefKey) return

      // Agrega por canal E por kind: "3 novas mensagens em Vendas" e
      // "2 atendimentos transferidos para Vendas" são avisos diferentes.
      const chave = `${evt.channel_id}|${evt.kind}`
      const lote = bufferRef.current.get(chave)
      if (lote) lote.eventos.push(evt)
      else bufferRef.current.set(chave, { eventos: [evt], prefKey })

      // Janela FIXA a partir do primeiro evento do lote, não debounce. Reagendar a
      // cada chegada empurraria o flush indefinidamente enquanto houvesse tráfego —
      // com 4 canais no escopo em horário de pico o aviso nunca sairia, que é o
      // oposto do que ele existe pra fazer. Aqui a janela nunca se estende: o lote
      // fecha 2s depois do primeiro, e o evento seguinte abre uma janela nova.
      if (!timerRef.current) timerRef.current = setTimeout(flush, FLUSH_DELAY_MS)
    }

    socket.on('channel_activity', onActivity)
    return () => {
      socket.off('channel_activity', onActivity)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
      bufferRef.current = new Map()
    }
  }, [socket])
}
